import {
  Avatar,
  Box,
  Button,
  Flex,
  HStack,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  Text,
  useColorModeValue,
} from '@chakra-ui/react';
import {
  Clock,
  Eye,
  FileBadge,
  FileChartColumnIncreasing,
  FileCheck,
  FileText,
  Mail,
  FileChartColumnIcon,
} from 'lucide-react';
import EnrolmentDocumentButtonAsync from '@duke-hq/ui/components/PdfGeneratorButton/EnrolmentDocumentButtonAsync';
import type {
  DocumentType,
  DocumentComponentProps,
} from '@duke-hq/ui/components/PdfGeneratorButton/utils';
import { getUserFullName } from '@duke-hq/lib';

export interface EnrolmentDocumentsModalLabels {
  title: string;
  emptyMessage: string;
  view: string;
  done: string;
}

const defaultLabels: EnrolmentDocumentsModalLabels = {
  title: 'Enrolment Documents',
  emptyMessage: "This enrolment doesn't have any documents available at the moment.",
  view: 'View',
  done: 'Done',
};

const iconMap: Partial<Record<DocumentType, React.JSX.Element>> = {
  certificateAndRecordOfResults: <FileBadge color="gray" />,
  educationVerification: <FileCheck color="gray" />,
  progressReport: <FileChartColumnIncreasing color="gray" />,
  statementOfAttainment: <FileText color="gray" />,
  nonVocationalCertificate: <FileBadge color="gray" />,
  attendanceReport: <FileChartColumnIncreasing color="gray" />,
  confirmationOfEnrolment: <FileChartColumnIcon color="gray" />,
  studentTimesheet: <Clock color="gray" />,
};

function DocumentRowIcon({ documentType }: { documentType: DocumentType }) {
  return iconMap[documentType] ?? null;
}

type GetPdfDocumentDataFn = (
  params: {
    documentType: DocumentType;
    enrolmentId: number;
    semester?: number;
  },
  options: { enabled: boolean; retry: boolean },
) => { refetch: () => Promise<{ data?: DocumentComponentProps }> };

function EnrolmentInfoCard({
  enrolment,
}: {
  enrolment: {
    user: { first_name: string; last_name: string | null; email: string | null };
    course_segment: string;
    course?: { identifier: string | null; title: string } | null;
  };
}): React.ReactElement {
  const studentName = getUserFullName({
    first_name: enrolment.user.first_name,
    last_name: enrolment.user.last_name,
  });

  return (
    <Box borderWidth="1px" borderRadius="md" p={4} w="full">
      <Flex align="center" gap={4}>
        <Avatar name={studentName} size="md" />
        <Box>
          <HStack spacing={2} align="center" mb={1}>
            <Text fontWeight="bold">{studentName}</Text>

            <HStack spacing={1}>
              <Mail size="16" />
              <Text fontSize="sm" color="gray.600">
                {enrolment.user.email}
              </Text>
            </HStack>
          </HStack>
          <HStack spacing={2} wrap="wrap">
            <Text fontSize="sm" color="gray.600">
              Enrolment:
            </Text>
            <Text fontSize="sm" color="gray.500" fontWeight="medium">
              {enrolment.course_segment}
            </Text>
            <Text fontSize="sm" color="gray.700">
              {enrolment.course?.identifier}
            </Text>
            <Text fontSize="sm" fontWeight="medium">
              {enrolment.course?.title}
            </Text>
          </HStack>
        </Box>
      </Flex>
    </Box>
  );
}

function EnrolmentDocumentsModal({
  enrolment,
  isOpen,
  onClose,
  getPdfDocumentDataFn,
  availableDocuments,
  labels,
}: {
  isOpen: boolean;
  onClose: () => void;
  enrolment: {
    id: number;
    tenant_id: number | null;
    user: { first_name: string; last_name: string | null; email: string | null };
    course_segment: string;
    course?: { identifier: string | null; title: string } | null;
  };
  availableDocuments: {
    documentType: DocumentType;
    documentDisplayName: string;
    documentSemester?: 1 | 2;
  }[];
  getPdfDocumentDataFn: GetPdfDocumentDataFn;
  labels?: Partial<EnrolmentDocumentsModalLabels>;
}): React.ReactElement {
  const l = { ...defaultLabels, ...labels };
  const enrolmentId = enrolment.id;
  const bg = useColorModeValue('gray.50', 'gray.700');
  const borderColor = useColorModeValue('gray.200', 'gray.600');

  return (
    <Modal size="2xl" isOpen={isOpen} onClose={onClose}>
      <ModalOverlay />
      <ModalContent as="form" padding={4} minH="300px" noValidate>
        <ModalHeader>
          {l.title}
          <ModalCloseButton margin={4} />
        </ModalHeader>
        <ModalBody pb={6} h="full">
          <EnrolmentInfoCard enrolment={enrolment} />
          {availableDocuments.length === 0 || availableDocuments == null ? (
            <Box
              borderWidth="1px"
              borderRadius="md"
              bg={bg}
              px={4}
              py={3}
              w="full"
              borderColor={borderColor}
              mt={4}
            >
              <Text color="fg.muted">{l.emptyMessage}</Text>
            </Box>
          ) : (
            availableDocuments.map(({ documentDisplayName, documentType, documentSemester }) => (
              <Box
                key={documentType}
                borderWidth="1px"
                borderRadius="md"
                bg={bg}
                px={4}
                py={3}
                w="full"
                borderColor={borderColor}
                mt={4}
              >
                <Flex w="full" justifyContent="space-between" alignItems="center">
                  <HStack align="center" justifyContent="space-between" width="full">
                    <HStack spacing={3}>
                      <DocumentRowIcon documentType={documentType} />
                      <Text fontWeight="medium">{documentDisplayName}</Text>
                    </HStack>
                    <EnrolmentDocumentButtonAsync
                      procedureQuery={getPdfDocumentDataFn}
                      documentType={documentType}
                      enrolmentId={enrolmentId}
                      semester={documentSemester}
                      gap="4px"
                      leftIcon={<Eye size={16} />}
                      variant="solid"
                    >
                      {l.view}
                    </EnrolmentDocumentButtonAsync>
                  </HStack>
                </Flex>
              </Box>
            ))
          )}
        </ModalBody>
        <ModalFooter gap={4}>
          <Button type="button" onClick={onClose}>
            {l.done}
          </Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
}

export default EnrolmentDocumentsModal;
